/* eslint-disable no-console, no-shadow, no-underscore-dangle, no-restricted-syntax, no-unused-vars */
import * as MutationHelpers from 'shared/helpers/vuex/mutationHelpers';
import types from '../mutation-types';
import CampaignsAPI from '../../api/campaigns';
import AnalyticsHelper from '../../helper/AnalyticsHelper';
import { CAMPAIGNS_EVENTS } from '../../helper/AnalyticsHelper/events';

// KPIs de entregas são CUMULATIVOS (funil: sent ⊇ delivered ⊇ read — mesmo
// contrato do build_delivery_stats no backend). Cada status raw contribui
// pra um conjunto de buckets; um recibo delivered/read NÃO decrementa
// "Enviadas" (a contabilidade disjunta antiga fazia o card minguar ao vivo
// e divergir do reload). Statuses do hold não tocam os KPIs de envio.
const DELIVERY_STAT_CONTRIBUTIONS = {
  sent: ['sent'],
  delivered: ['sent', 'delivered'],
  read: ['sent', 'delivered', 'read'],
  failed: ['failed'],
};

const applyDeliveryStatusTransition = (stats, oldStatus, newStatus) => {
  const next = { ...stats };
  (DELIVERY_STAT_CONTRIBUTIONS[oldStatus] || []).forEach(key => {
    if (next[key] > 0) next[key] -= 1;
  });
  (DELIVERY_STAT_CONTRIBUTIONS[newStatus] || []).forEach(key => {
    next[key] = (next[key] || 0) + 1;
  });
  return next;
};

export const state = {
  records: [],
  deliveries: [],
  // ✅ NOVO: Lista de schedules para paginação server-side
  allSchedules: [],
  // ✅ Metadados de paginação para campanhas
  meta: {
    current_page: 1,
    per_page: 25,
    total_count: 0,
    total_pages: 1,
  },
  // ✅ NOVO: Metadados de paginação para schedules
  schedulesMeta: {
    current_page: 1,
    per_page: 25,
    total_count: 0,
    total_pages: 1,
  },
  // NOVO: Estatísticas agregadas dos schedules
  schedulesStats: {
    total: 0,
    scheduled: 0,
    running: 0,
    completed: 0,
    failed: 0,
    cancelled: 0,
  },
  // NOVO: Opções de status para filtros (com value e label)
  schedulesStatusOptions: [],
  // ✅ NOVO: Metadados de paginação para deliveries (separado)
  deliveriesMeta: {
    current_page: 1,
    per_page: 50,
    total_count: 0,
    total_pages: 1,
  },
  // ✅ NOVO: Estatísticas agregadas dos deliveries (do servidor)
  deliveriesStats: {
    total: 0,
    billable: 0,
    sent: 0,
    delivered: 0,
    read: 0,
    failed: 0,
  },
  // ✅ NOVO: Template pricing category
  templatePricingCategory: {},
  campaignData: {
    // ✅ Dados para campanha API oficial (existente)
    selectedTable: '',
    selectedPhoneColumn: '',
    selectedTag: 0,
    filters: [],
    placeholders: {},
    template: null,
    schedule: null,
    messageLimit: null,
    campaignName: null,
    selectedContactColumn: null,

    // ✅ NOVO: Dados para campanha WhatsApp normal
    whatsappNormal: {
      selectedLabels: [],
      selectedInbox: '',
      webhookUrl: '',
      message: '',
      attachedFiles: [],
      currentStep: 'config',
      campaignName: '',
      schedule: null,
      messageLimit: null,
    },
  },
  // Cache das colunas por tabela
  cachedTableColumns: {},
  audiencePreview: null,

  uiFlags: {
    isFetching: false,
    isCreating: false,
    isDeleting: false,
    isDeletingScheduling: false,
    isStarting: false,
    isStopping: false,
    isFetchingDeliveries: false,
    isFetchingSchedules: false, // ✅ NOVO: Flag para carregamento de schedules
  },
};

export const getters = {
  getUIFlags(_state) {
    return _state.uiFlags;
  },
  // ✅ Metadados de paginação
  getCampaignsMeta(_state) {
    return _state.meta;
  },
  getCampaigns: _state => campaignType => {
    return _state.records
      .filter(record => record.campaign_type === campaignType)
      .sort((a1, a2) => a1.id - a2.id);
  },
  getAllCampaigns: _state => {
    return _state.records;
  },
  getAllCampaignsWhatsappApiBayles: _state => {
    return _state.records
      .filter(record => record.campaign_type === 'whatsapp_bayles')
      .sort((a, b) => a.id - b.id);
  },
  getAllCampaignsWhatsappApiOficial: _state => {
    const records = Array.isArray(_state.records) ? _state.records : [];
    return records
      .filter(record => {
        return record.campaign_type === 'whatsapp_official';
      })
      .sort((a, b) => a.id - b.id);
  },
  getCampaignData: _state => {
    return _state.campaignData;
  },
  getMessageLimit: _state => {
    return _state.campaignData.messageLimit;
  },
  getTemplate: _state => {
    return _state.campaignData.template;
  },
  getCampaignScheduleDeliveries: _state => {
    return _state.deliveries;
  },

  // ✅ NOVOS GETTERS: Paginação server-side para deliveries
  getDeliveriesMeta: _state => {
    return _state.deliveriesMeta;
  },

  getDeliveriesStats: _state => {
    return _state.deliveriesStats;
  },

  getTemplatePricingCategory: _state => {
    return _state.templatePricingCategory;
  },

  // ✅ NOVOS GETTERS: Paginação server-side para schedules
  getAllSchedules: _state => {
    return _state.allSchedules;
  },

  getSchedulesMeta: _state => {
    return _state.schedulesMeta;
  },

  getSchedulesStats: _state => {
    return _state.schedulesStats;
  },

  getSchedulesStatusOptions: _state => {
    return _state.schedulesStatusOptions;
  },

  // ✅ NOVOS GETTERS: Para campanha WhatsApp normal
  getWhatsappNormalCampaignData: _state => {
    return _state.campaignData.whatsappNormal;
  },

  getWhatsappOficialCampaignData: _state => {
    const { whatsappNormal, ...whatsappOficialData } = _state.campaignData;
    return whatsappOficialData;
  },

  getWhatsappNormalCurrentStep: _state => {
    return _state.campaignData.whatsappNormal.currentStep;
  },

  isWhatsappNormalConfigValid: _state => {
    const { selectedLabels, selectedInbox, webhookUrl } =
      _state.campaignData.whatsappNormal;
    return selectedLabels.length > 0 && selectedInbox && webhookUrl;
  },

  isWhatsappNormalMessageValid: _state => {
    const { message, attachedFiles } = _state.campaignData.whatsappNormal;
    return message.length > 0 || (attachedFiles && attachedFiles.length > 0);
  },

  getScheduleStatusData: state => (campaignId, scheduleId) => {
    const campaign = state.records.find(c => c.id === campaignId);
    if (campaign && campaign.campaign_schedules) {
      const schedule = campaign.campaign_schedules.find(
        s => s.id === scheduleId
      );
      return schedule?.status_data || null;
    }
    return null;
  },

  hasProgressData: state => (campaignId, scheduleId) => {
    const campaign = state.records.find(c => c.id === campaignId);
    if (campaign && campaign.campaign_schedules) {
      const schedule = campaign.campaign_schedules.find(
        s => s.id === scheduleId
      );
      return schedule?.has_progress_data || false;
    }
    return false;
  },

  getRunningCampaigns: state => {
    const runningSchedules = [];
    state.records.forEach(campaign => {
      if (campaign.campaign_schedules) {
        campaign.campaign_schedules.forEach(schedule => {
          if (
            schedule.campaign_status_raw === 'running' ||
            schedule.campaign_status === 'running'
          ) {
            runningSchedules.push({
              campaignId: campaign.id,
              campaignTitle: campaign.title,
              scheduleId: schedule.id,
              schedule: schedule,
            });
          }
        });
      }
    });
    return runningSchedules;
  },

  // Cache de colunas
  getCachedTableColumns: state => tableName => {
    return state.cachedTableColumns[tableName] || null;
  },

  isTableColumnsCached: state => tableName => {
    const cached = state.cachedTableColumns[tableName];
    if (!cached) return false;

    // Verificar se o cache não está muito antigo (1 hora)
    const oneHour = 60 * 60 * 1000;
    return Date.now() - cached.cachedAt < oneHour;
  },

  getAllCachedTables: state => {
    return Object.keys(state.cachedTableColumns);
  },

  getAudiencePreview: state => {
    return state.audiencePreview;
  },

  isPreviewingAudience: state => {
    return state.uiFlags.isPreviewingAudience;
  },
};

export const actions = {
  updateDeliveryByMessageId(
    { commit, state },
    { messageId, status, updatedAt }
  ) {
    const deliveryIndex = state.deliveries.findIndex(
      d => d.message_id === messageId
    );

    if (deliveryIndex !== -1) {
      commit(types.UPDATE_DELIVERY_BY_MESSAGE_ID, {
        deliveryIndex,
        status,
        updatedAt,
        messageId,
      });
    }
  },

  get: async function getCampaigns({ commit }, params = {}) {
    commit(types.SET_CAMPAIGN_UI_FLAG, { isFetching: true });
    try {
      // ✅ A API agora retorna formato paginado por padrão
      const response = params.include_schedules
        ? await CampaignsAPI.getWithSchedules()
        : await CampaignsAPI.get();

      // ✅ Detectar formato da resposta (paginado ou array simples)
      const data = response.data;
      if (data.payload && data.meta) {
        // Formato paginado: { meta: {...}, payload: [...] }
        commit(types.SET_CAMPAIGNS, data.payload);
        commit(types.SET_CAMPAIGN_META, data.meta);
      } else if (Array.isArray(data)) {
        // Formato antigo: array direto (compatibilidade)
        commit(types.SET_CAMPAIGNS, data);
      } else {
        console.warn('[campaigns] Formato de resposta desconhecido:', data);
        commit(types.SET_CAMPAIGNS, []);
      }
    } catch (error) {
      console.error('[campaigns] Error in get action:', error);
    } finally {
      commit(types.SET_CAMPAIGN_UI_FLAG, { isFetching: false });
    }
  },

  create: async function createCampaign({ commit }, campaignObj) {
    commit(types.SET_CAMPAIGN_UI_FLAG, { isCreating: true });
    try {
      const response = await CampaignsAPI.create(campaignObj);
      commit(types.ADD_CAMPAIGN, response.data);
    } catch (error) {
      console.error('[campaigns] Error in create action:', error);
      throw new Error(error);
    } finally {
      commit(types.SET_CAMPAIGN_UI_FLAG, { isCreating: false });
    }
  },

  // Builder node-based: validação/compile no BACKEND (padrão Studio). O erro
  // axios sobe CRU (sem re-embrulhar) — o builder lê response.data
  // .validation_errors do 422 pra destacar os nodes com problema.
  createFromBuilder: async ({ commit }, { graph, dryRun = false }) => {
    if (dryRun) {
      const response = await CampaignsAPI.createFromBuilder(graph, {
        dryRun,
      });
      return response.data;
    }
    commit(types.SET_CAMPAIGN_UI_FLAG, { isCreating: true });
    try {
      const response = await CampaignsAPI.createFromBuilder(graph);
      commit(types.ADD_CAMPAIGN, response.data);
      return response.data;
    } finally {
      commit(types.SET_CAMPAIGN_UI_FLAG, { isCreating: false });
    }
  },

  update: async ({ commit }, { id, ...updateObj }) => {
    commit(types.SET_CAMPAIGN_UI_FLAG, { isUpdating: true });
    try {
      const response = await CampaignsAPI.update(id, updateObj);
      AnalyticsHelper.track(CAMPAIGNS_EVENTS.UPDATE_CAMPAIGN);
      commit(types.EDIT_CAMPAIGN, response.data);
    } catch (error) {
      console.error('[campaigns] Error in update action:', error);
      throw new Error(error);
    } finally {
      commit(types.SET_CAMPAIGN_UI_FLAG, { isUpdating: false });
    }
  },

  delete: async ({ commit, dispatch, state }, id) => {
    commit(types.SET_CAMPAIGN_UI_FLAG, { isDeleting: true });
    try {
      const response = await CampaignsAPI.delete(id);
      AnalyticsHelper.track(CAMPAIGNS_EVENTS.DELETE_CAMPAIGN);

      // Verificar se é delete assíncrono (status 202)
      if (response.status === 202 && response.data?.status === 'deleting') {
        // Remover imediatamente da UI — o job finaliza em background e o ActionCable
        // emitirá campaign_deleted (DELETE_CAMPAIGN) novamente, que será no-op.
        commit(types.DELETE_CAMPAIGN, id);
        return { async: true, message: response.data.message };
      }

      // Verificar se é delete parcial (apenas schedules pendentes deletados)
      if (response.status === 200 && response.data?.status === 'partial') {
        // Refetch para obter dados corretos do servidor
        await dispatch('listAllSchedules', {
          page: state.schedulesMeta.current_page || 1,
          perPage: state.schedulesMeta.per_page || 25,
          channelType: 'Channel::Whatsapp',
        });
        // Remover da UI mesmo no caso parcial — o usuário clicou em "deletar campanha"
        commit(types.DELETE_CAMPAIGN, id);
        return {
          partial: true,
          message: response.data.message,
          deleted_count: response.data.deleted_count,
          kept_count: response.data.kept_count,
        };
      }

      // Delete síncrono completo (status 204) - refetch para atualizar dados
      await dispatch('listAllSchedules', {
        page: 1, // Voltar para página 1 após delete
        perPage: state.schedulesMeta.per_page || 25,
        channelType: 'Channel::Whatsapp',
      });
      // Também remover da lista de records
      commit(types.DELETE_CAMPAIGN, id);
      return { async: false, refetched: true };
    } catch (error) {
      console.error('[campaigns/delete] Erro:', error);
      throw new Error(error);
    } finally {
      commit(types.SET_CAMPAIGN_UI_FLAG, { isDeleting: false });
    }
  },

  deleteScheduling: async ({ commit }, { campaignId, schedulingId }) => {
    commit(types.SET_CAMPAIGN_UI_FLAG, { isDeletingScheduling: true });
    try {
      await CampaignsAPI.deleteScheduling(campaignId, schedulingId);
      commit(types.DELETE_CAMPAIGN_SCHEDULING, { campaignId, schedulingId });
    } catch (error) {
      console.error('[campaigns] Error in deleteScheduling action:', error);
      throw new Error(error);
    } finally {
      commit(types.SET_CAMPAIGN_UI_FLAG, { isDeletingScheduling: false });
    }
  },

  // Exclui os agendamentos pendentes de UMA campanha (o histórico fica).
  // Retorna o deleted_count REAL do backend — o caller reporta no toast.
  deletePendingSchedules: async ({ commit }, { campaignId }) => {
    commit(types.SET_CAMPAIGN_UI_FLAG, { isDeletingScheduling: true });
    try {
      const response = await CampaignsAPI.deletePendingSchedules(campaignId);
      return response.data?.deleted_count ?? 0;
    } finally {
      commit(types.SET_CAMPAIGN_UI_FLAG, { isDeletingScheduling: false });
    }
  },

  startCampaign: async ({ commit }, { campaignId, schedulingId }) => {
    commit(types.SET_CAMPAIGN_UI_FLAG, { isStarting: true });
    try {
      await CampaignsAPI.startCampaign(campaignId, schedulingId);
      commit(types.START_CAMPAIGN, { campaignId, schedulingId });
      return true;
    } catch (error) {
      console.error('[campaigns] Error in startCampaign action:', error);
      throw new Error(error);
    } finally {
      commit(types.SET_CAMPAIGN_UI_FLAG, { isStarting: false });
    }
  },

  stopCampaign: async ({ commit }, { campaignId, schedulingId }) => {
    commit(types.SET_CAMPAIGN_UI_FLAG, { isStopping: true });
    try {
      await CampaignsAPI.stopCampaign(campaignId, schedulingId);
      commit(types.STOP_CAMPAIGN, { campaignId, schedulingId });
      AnalyticsHelper.track(CAMPAIGNS_EVENTS.STOP_CAMPAIGN);
      return true;
    } catch (error) {
      console.error('[campaigns] Error in stopCampaign action:', error);
      throw new Error(error);
    } finally {
      commit(types.SET_CAMPAIGN_UI_FLAG, { isStopping: false });
    }
  },

  // ✅ NOVA ACTION: Listar todos os schedules com paginação server-side
  listAllSchedules: async (
    { commit },
    {
      page = 1,
      perPage = 25,
      search = '',
      status = null,
      channelType = null,
      campaignType = null,
      startDate = null,
      endDate = null,
    } = {}
  ) => {
    commit(types.SET_CAMPAIGN_UI_FLAG, { isFetchingSchedules: true });
    try {
      const response = await CampaignsAPI.listAllSchedules({
        page,
        per_page: perPage,
        search,
        status,
        channel_type: channelType,
        campaign_type: campaignType,
        start_date: startDate,
        end_date: endDate,
      });
      commit(types.SET_ALL_SCHEDULES, response.data);
      return response.data;
    } catch (error) {
      console.error('[campaigns] Error in listAllSchedules action:', error);
      throw new Error(error);
    } finally {
      commit(types.SET_CAMPAIGN_UI_FLAG, { isFetchingSchedules: false });
    }
  },

  listCampaignScheduleDeliveries: async (
    { commit },
    {
      campaignId,
      schedulingId,
      page = 1,
      perPage = 50,
      search = '',
      status = null,
    }
  ) => {
    commit(types.SET_CAMPAIGN_UI_FLAG, { isFetchingDeliveries: true });
    try {
      const response = await CampaignsAPI.listCampaignScheduleDeliveries(
        campaignId,
        schedulingId,
        {
          page,
          per_page: perPage,
          search,
          status,
        }
      );
      commit(types.SET_CAMPAIGN_SCHEDULE_DELIVERIES, response.data);
      return response.data;
    } catch (error) {
      console.error(
        '[campaigns] Error in listCampaignScheduleDeliveries action:',
        error
      );
      throw new Error(error);
    } finally {
      commit(types.SET_CAMPAIGN_UI_FLAG, { isFetchingDeliveries: false });
    }
  },

  // Actions para campanha API oficial (existente)
  saveCampaignData({ commit }, data) {
    commit(types.SET_CAMPAIGN_DATA, data);
  },

  clearCampaignData({ commit, dispatch }) {
    commit(types.CLEAR_CAMPAIGN_DATA);
    // ✅ Limpar dados do Google Sheets também (preview, columns cache, etc.)
    dispatch('googleSheets/clearData', null, { root: true });
  },

  // ✅ NOVAS ACTIONS: Para campanha WhatsApp normal
  saveWhatsappNormalCampaignData({ commit }, data) {
    commit(types.SET_WHATSAPP_NORMAL_CAMPAIGN_DATA, data);
  },

  clearWhatsappNormalCampaignData({ commit }) {
    commit(types.CLEAR_WHATSAPP_NORMAL_CAMPAIGN_DATA);
  },

  setWhatsappNormalCurrentStep({ commit }, step) {
    commit(types.SET_WHATSAPP_NORMAL_CURRENT_STEP, step);
  },

  // Actions para cache de colunas
  setCachedTableColumns(
    { commit },
    { tableName, phoneColumns, nameColumns, allColumns }
  ) {
    commit(types.SET_CACHED_TABLE_COLUMNS, {
      tableName,
      phoneColumns: Array.isArray(phoneColumns) ? phoneColumns : [],
      nameColumns: Array.isArray(nameColumns) ? nameColumns : [],
      allColumns: Array.isArray(allColumns) ? allColumns : [],
    });
  },

  clearCachedTableColumns({ commit }, tableName = null) {
    commit(types.CLEAR_CACHED_TABLE_COLUMNS, tableName);
  },

  clearExpiredColumnCache({ commit, state }) {
    const oneHour = 60 * 60 * 1000;
    const now = Date.now();

    Object.keys(state.cachedTableColumns).forEach(tableName => {
      const cached = state.cachedTableColumns[tableName];
      if (cached && now - cached.cachedAt > oneHour) {
        commit(types.CLEAR_CACHED_TABLE_COLUMNS, tableName);
      }
    });
  },

  updateCampaignStatus(
    { commit, state },
    { scheduleId, status, statusLabel = null, statusData = null }
  ) {
    const campaigns = state.records;

    for (const campaign of campaigns) {
      if (campaign.campaign_schedules) {
        const schedule = campaign.campaign_schedules.find(
          s => s.id === scheduleId
        );
        if (schedule) {
          // Contrato: campaign_status guarda o enum CRU; o rótulo traduzido do
          // evento (status_label) fica em campaign_status_label pra exibição.
          const updateData = {
            ...schedule,
            campaign_status: status,
            campaign_status_raw: status,
          };

          if (statusLabel) {
            updateData.campaign_status_label = statusLabel;
          }

          if (statusData) {
            updateData.status_data = statusData;
            // Broadcasts nem sempre trazem todos os campos (o terminal do
            // model pode vir sem items_*) — o `|| 0` antigo ZERAVA o
            // registro no store. Só sobrescreve o que veio de fato.
            const assignIfPresent = (key, value) => {
              if (value !== undefined && value !== null) {
                updateData[key] = value;
              }
            };
            assignIfPresent('progress_percentage', statusData.progress);
            assignIfPresent('total_items', statusData.total_items);
            assignIfPresent(
              'items_processed',
              statusData.items_processed ?? statusData.items_sent
            );
            assignIfPresent('items_sent', statusData.items_sent);
            assignIfPresent('items_failed', statusData.items_failed);
            assignIfPresent('current_batch', statusData.current_batch);
            assignIfPresent('total_batches', statusData.total_batches);
            updateData.status_message = statusData.message;
            updateData.has_progress_data = true;
            updateData.last_status_update =
              statusData.last_updated_at || new Date().toISOString();
          }

          commit(types.UPDATE_CAMPAIGN_SCHEDULE, {
            campaignId: campaign.id,
            schedule: updateData,
          });
          break;
        }
      }
    }
  },

  // ✅ NOVA ACTION: Preview da audiência
  previewAudience: async ({ commit }, { audience, limit = 200 }) => {
    commit(types.SET_CAMPAIGN_UI_FLAG, { isPreviewingAudience: true });

    try {
      const response = await CampaignsAPI.previewAudience(audience, limit);

      commit(types.SET_AUDIENCE_PREVIEW, response.data.preview);

      return response.data.preview;
    } catch (error) {
      console.error('[campaigns] Error in previewAudience action:', error);
      commit(types.SET_AUDIENCE_PREVIEW, null);
      throw error;
    } finally {
      commit(types.SET_CAMPAIGN_UI_FLAG, { isPreviewingAudience: false });
    }
  },

  // ✅ NOVA ACTION: Limpar preview
  clearAudiencePreview: ({ commit }) => {
    commit(types.SET_AUDIENCE_PREVIEW, null);
  },
};

export const mutations = {
  [types.SET_AUDIENCE_PREVIEW](state, previewData) {
    state.audiencePreview = previewData;
  },

  [types.SET_CAMPAIGN_UI_FLAG](_state, data) {
    _state.uiFlags = {
      ..._state.uiFlags,
      ...data,
    };
  },

  [types.SET_CAMPAIGNS](_state, data) {
    MutationHelpers.set(_state, data);
  },

  // ✅ Mutation para metadados de paginação
  [types.SET_CAMPAIGN_META](_state, meta) {
    _state.meta = { ..._state.meta, ...meta };
  },

  [types.ADD_CAMPAIGN]: MutationHelpers.create,
  [types.EDIT_CAMPAIGN]: MutationHelpers.update,
  [types.DELETE_CAMPAIGN](state, id) {
    // Apenas remover campanha de records - o refetch já atualizou allSchedules, schedulesMeta e schedulesStats
    MutationHelpers.destroy(state, id);
  },

  // Marcar campanha como "deleting" (exclusão assíncrona em andamento)
  [types.SET_CAMPAIGN_DELETING](state, campaignId) {
    const campaign = state.records.find(c => c.id === campaignId);
    if (campaign) {
      campaign._deleting = true;
    }
  },

  [types.DELETE_CAMPAIGN_SCHEDULING](state, { campaignId, schedulingId }) {
    // Usar splice para substituir o objeto no array e garantir reatividade do Vue 3.
    // Mutação in-place (find + assign) não invalida getters que dependem de state.records.
    const index = state.records.findIndex(c => c.id === campaignId);
    if (index !== -1) {
      const campaign = state.records[index];
      if (campaign.campaign_schedules) {
        state.records.splice(index, 1, {
          ...campaign,
          campaign_schedules: campaign.campaign_schedules.filter(
            schedule => schedule.id !== schedulingId
          ),
        });
      }
    }

    // ✅ NOVO: Remover também de allSchedules (paginação server-side)
    if (state.allSchedules && state.allSchedules.length > 0) {
      state.allSchedules = state.allSchedules.filter(
        schedule =>
          schedule.id !== schedulingId && schedule.schedule_id !== schedulingId
      );

      // ✅ Usar spread operator para garantir reatividade do Vue
      state.schedulesMeta = {
        ...state.schedulesMeta,
        total_count: Math.max(0, (state.schedulesMeta.total_count || 0) - 1),
      };

      state.schedulesStats = {
        ...state.schedulesStats,
        scheduled: Math.max(0, (state.schedulesStats.scheduled || 0) - 1),
        total: Math.max(0, (state.schedulesStats.total || 0) - 1),
      };
    }
  },

  // Remove apenas schedules "scheduled" (Agendado) de uma campanha específica
  [types.DELETE_SCHEDULED_SCHEDULES_BY_CAMPAIGN](state, campaignId) {
    // Remover de records (campanhas) - apenas schedules "scheduled"
    const campaign = state.records.find(c => c.id === campaignId);
    if (campaign && campaign.campaign_schedules) {
      campaign.campaign_schedules = campaign.campaign_schedules.filter(
        schedule =>
          schedule.campaign_status_raw !== 'scheduled' &&
          schedule.campaign_status !== 'scheduled'
      );
    }

    // Remover de allSchedules - apenas schedules "scheduled"
    if (state.allSchedules && state.allSchedules.length > 0) {
      const schedulesToRemove = state.allSchedules.filter(
        schedule =>
          schedule.campaign_id === campaignId &&
          (schedule.campaign_status_raw === 'scheduled' ||
            schedule.status === 'scheduled' ||
            schedule.campaign_status === 'scheduled')
      );
      const countToRemove = schedulesToRemove.length;

      if (countToRemove > 0) {
        state.allSchedules = state.allSchedules.filter(
          schedule =>
            !(
              schedule.campaign_id === campaignId &&
              (schedule.campaign_status_raw === 'scheduled' ||
                schedule.status === 'scheduled' ||
                schedule.campaign_status === 'scheduled')
            )
        );

        // Usar spread operator para garantir reatividade do Vue
        state.schedulesMeta = {
          ...state.schedulesMeta,
          total_count: Math.max(
            0,
            (state.schedulesMeta.total_count || 0) - countToRemove
          ),
        };

        state.schedulesStats = {
          ...state.schedulesStats,
          scheduled: Math.max(
            0,
            (state.schedulesStats.scheduled || 0) - countToRemove
          ),
          total: Math.max(0, (state.schedulesStats.total || 0) - countToRemove),
        };
      }
    }
  },

  [types.START_CAMPAIGN](state, { campaignId, schedulingId }) {
    const campaign = state.records.find(c => c.id === campaignId);
    if (campaign && campaign.campaign_schedules) {
      const schedule = campaign.campaign_schedules.find(
        s => s.id === schedulingId
      );
      if (schedule) {
        // Update otimista — enum CRU. O rótulo é derivado por i18n na exibição.
        schedule.campaign_status = 'running';
        schedule.campaign_status_raw = 'running';
        schedule.campaign_status_label = null;
      }
    }
  },

  [types.STOP_CAMPAIGN](state, { campaignId, schedulingId }) {
    const campaign = state.records.find(c => c.id === campaignId);
    if (campaign && campaign.campaign_schedules) {
      const schedule = campaign.campaign_schedules.find(
        s => s.id === schedulingId
      );
      if (schedule) {
        // Update otimista — enum CRU. O rótulo é derivado por i18n na exibição.
        schedule.campaign_status = 'cancelled';
        schedule.campaign_status_raw = 'cancelled';
        schedule.campaign_status_label = null;
      }
    }
  },

  // NOVA MUTATION: Para paginação server-side de schedules
  [types.SET_ALL_SCHEDULES](_state, data) {
    if (data.schedules !== undefined) {
      _state.allSchedules = data.schedules || [];
      _state.schedulesMeta = data.meta || {
        current_page: 1,
        per_page: 25,
        total_count: 0,
        total_pages: 1,
      };
      _state.schedulesStats = data.stats || {
        total: 0,
        scheduled: 0,
        running: 0,
        completed: 0,
        failed: 0,
        cancelled: 0,
      };
      _state.schedulesStatusOptions = data.status_options || [];
    } else {
      _state.allSchedules = Array.isArray(data) ? data : [];
    }
  },

  [types.SET_CAMPAIGN_SCHEDULE_DELIVERIES](_state, data) {
    // ✅ NOVO: Formato com paginação server-side
    if (data.deliveries !== undefined) {
      _state.deliveries = data.deliveries || [];
      _state.deliveriesMeta = data.meta || {
        current_page: 1,
        per_page: 50,
        total_count: 0,
        total_pages: 1,
      };
      _state.deliveriesStats = data.stats || {
        total: 0,
        billable: 0,
        sent: 0,
        delivered: 0,
        read: 0,
        failed: 0,
      };
      _state.templatePricingCategory = data.template_pricing_category || {};
    } else {
      // ✅ FALLBACK: Formato antigo (array direto) para compatibilidade
      _state.deliveries = Array.isArray(data) ? data : [];
    }
  },

  [types.UPDATE_CAMPAIGN_SCHEDULE_DELIVERY](state, { id, status }) {
    const deliveryIndex = state.deliveries.findIndex(d => d.id === id);
    if (deliveryIndex !== -1) {
      const oldStatus = state.deliveries[deliveryIndex].status;
      const newDeliveries = [...state.deliveries];
      newDeliveries[deliveryIndex] = {
        ...newDeliveries[deliveryIndex],
        status,
      };
      state.deliveries = newDeliveries;

      if (oldStatus !== status && state.deliveriesStats) {
        state.deliveriesStats = applyDeliveryStatusTransition(
          state.deliveriesStats,
          oldStatus,
          status
        );
      }
    }
  },

  // Mutations para campanha API oficial (existente)
  [types.SET_CAMPAIGN_DATA](_state, data) {
    _state.campaignData = { ..._state.campaignData, ...data };
  },

  [types.UPDATE_DELIVERY_BY_MESSAGE_ID](
    state,
    { deliveryIndex, status, updatedAt }
  ) {
    const oldStatus = state.deliveries[deliveryIndex].status;
    const updatedDeliveries = [...state.deliveries];

    updatedDeliveries[deliveryIndex] = {
      ...updatedDeliveries[deliveryIndex],
      status: status,
      updated_at: updatedAt || new Date().toISOString(),
    };

    state.deliveries = updatedDeliveries;

    if (oldStatus !== status && state.deliveriesStats) {
      state.deliveriesStats = applyDeliveryStatusTransition(
        state.deliveriesStats,
        oldStatus,
        status
      );
    }
  },

  [types.CLEAR_CAMPAIGN_DATA](_state) {
    _state.campaignData = {
      ..._state.campaignData,
      selectedTable: '',
      selectedPhoneColumn: '',
      selectedTag: 0,
      // Destino da resposta (IA) — sem o reset, o destino de uma campanha
      // vazava em silêncio pra próxima criada na mesma sessão.
      selectedDestination: 'default',
      // Mesmo vazamento: modo de conversa aberta + janela de release.
      openConversationMode: 'hold',
      releaseWindow: null,
      filters: [],
      placeholders: {},
      template: null,
      schedule: null,
      messageLimit: null,
      campaignName: null,
      selectedContactColumn: null,
      // ✅ Limpar dados do Google Sheets também
      filterData: null,
    };
  },

  // ✅ NOVAS MUTATIONS: Para campanha WhatsApp normal
  [types.SET_WHATSAPP_NORMAL_CAMPAIGN_DATA](_state, data) {
    _state.campaignData.whatsappNormal = {
      ..._state.campaignData.whatsappNormal,
      ...data,
    };
  },

  [types.CLEAR_WHATSAPP_NORMAL_CAMPAIGN_DATA](_state) {
    _state.campaignData.whatsappNormal = {
      selectedLabels: [],
      selectedInbox: '',
      webhookUrl: '',
      message: '',
      attachedFiles: [],
      currentStep: 'config',
      campaignName: '',
      schedule: null,
      messageLimit: null,
    };
  },

  [types.SET_WHATSAPP_NORMAL_CURRENT_STEP](_state, step) {
    _state.campaignData.whatsappNormal.currentStep = step;
  },

  [types.UPDATE_CAMPAIGN_SCHEDULE](state, { campaignId, schedule }) {
    const campaign = state.records.find(c => c.id === campaignId);
    if (campaign && campaign.campaign_schedules) {
      const scheduleIndex = campaign.campaign_schedules.findIndex(
        s => s.id === schedule.id
      );
      if (scheduleIndex !== -1) {
        Object.assign(campaign.campaign_schedules[scheduleIndex], schedule);
      } else {
        console.warn(
          `[Mutation] Schedule ${schedule.id} não encontrado na campanha ${campaignId}`
        );
      }
    } else {
      console.warn(
        `[Mutation] Campanha ${campaignId} não encontrada ou sem schedules`
      );
    }
  },

  // Mutations para cache de colunas
  [types.SET_CACHED_TABLE_COLUMNS](
    state,
    { tableName, phoneColumns, nameColumns, allColumns }
  ) {
    state.cachedTableColumns = {
      ...state.cachedTableColumns,
      [tableName]: {
        phoneColumns: [...phoneColumns],
        nameColumns: [...nameColumns],
        allColumns: [...allColumns],
        cachedAt: Date.now(),
      },
    };
  },

  [types.CLEAR_CACHED_TABLE_COLUMNS](state, tableName = null) {
    if (tableName) {
      const newCache = { ...state.cachedTableColumns };
      delete newCache[tableName];
      state.cachedTableColumns = newCache;
    } else {
      state.cachedTableColumns = {};
    }
  },
};

export default {
  namespaced: true,
  actions,
  state,
  getters,
  mutations,
};
